import { InfiniteData, useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import { useEffect, useMemo, useRef } from "react"
import { DataResponse, MessageResponseDataSchema } from "schema"
import { z } from "zod"
import { getConversationsQueryKey } from "../lib/conversationsQuery"
import {
  ConversationTimelinePage,
  conversationTimelineQuery,
  getConversationTimelineQueryKey,
} from "../lib/conversationTimelineQuery"

type Message = z.infer<typeof MessageResponseDataSchema>

type Options = {
  enabled?: boolean
  refetchInterval?: number
  includeRaw?: boolean
}

function dedupeById<T extends { id: string }>(items: T[]) {
  return Array.from(new Map(items.map((item) => [item.id, item])).values())
}

function sortByCreatedAt<T extends { createdAt: string }>(items: T[]) {
  return [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
}

export function useConversationTimeline(conversationId: string | undefined, options: Options = {}) {
  const queryClient = useQueryClient()
  const enabled = options.enabled !== false && !!conversationId
  const includeRaw = options.includeRaw ?? false
  const previousPending = useRef<{
    conversationId: string | undefined
    messageIds: Set<string>
  } | null>(null)

  const infiniteQuery = useInfiniteQuery({
    ...conversationTimelineQuery(conversationId ?? "", { enabled, includeRaw }),
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.messages.some(
          (message) =>
            message.isOneTimeTemplate &&
            !message.sentAt &&
            !message.failedAt &&
            !message.externalDeletedAt,
        ),
      )
        ? 5000
        : false,
  })

  const pages = useMemo(() => infiniteQuery.data?.pages ?? [], [infiniteQuery])
  const messages = useMemo<Message[]>(
    () => sortByCreatedAt(dedupeById(pages.flatMap((page) => page.messages))),
    [pages],
  )

  useEffect(() => {
    const previous = previousPending.current
    const hasSentPendingMessage =
      previous !== null &&
      previous.conversationId === conversationId &&
      messages.some((message) => message.sentAt && previous.messageIds.has(message.id))

    previousPending.current = {
      conversationId,
      messageIds: new Set(
        messages
          .filter(
            (message) =>
              message.isOneTimeTemplate &&
              !message.sentAt &&
              !message.failedAt &&
              !message.externalDeletedAt,
          )
          .map((message) => message.id),
      ),
    }

    if (hasSentPendingMessage) {
      void queryClient.invalidateQueries({ queryKey: getConversationsQueryKey() })
    }
  }, [conversationId, messages, queryClient])

  const conversationTickets = useMemo(
    () => sortByCreatedAt(dedupeById(pages.flatMap((page) => page.conversationTickets))),
    [pages],
  )
  const conversationQuestions = useMemo(
    () => sortByCreatedAt(dedupeById(pages.flatMap((page) => page.conversationQuestions))),
    [pages],
  )
  const conversationOpportunities = useMemo(
    () => sortByCreatedAt(dedupeById(pages.flatMap((page) => page.conversationOpportunities))),
    [pages],
  )
  const conversationCapabilityExecutions = useMemo(
    () =>
      sortByCreatedAt(dedupeById(pages.flatMap((page) => page.conversationCapabilityExecutions))),
    [pages],
  )
  const customerLogs = useMemo(
    () => sortByCreatedAt(dedupeById(pages.flatMap((page) => page.customerLogs))),
    [pages],
  )

  const newestId = [...messages].reverse().find((message) => !message.id.startsWith("temp-"))?.id

  const pollQuery = useQuery({
    queryKey: [
      ...getConversationTimelineQueryKey(conversationId ?? "", includeRaw),
      "after",
      newestId ?? "",
    ],
    // Prevent stale poll results from another conversation from being merged
    // into this conversation's cache when the key changes.
    placeholderData: undefined,
    queryFn: async () => {
      const response = await axios.get<DataResponse<ConversationTimelinePage>>(
        `/conversations/${conversationId}/timeline`,
        {
          params: { afterMessageId: newestId, limit: 100, includeRaw },
        },
      )
      return response.data.data
    },
    enabled: enabled && !!newestId,
    refetchInterval: options.refetchInterval ?? 10000,
  })

  useEffect(() => {
    if (!conversationId || !pollQuery.data) return

    const {
      messages: newMessages,
      conversationTickets: newTickets,
      conversationQuestions: newQuestions,
      conversationOpportunities: newOpportunities,
      conversationCapabilityExecutions: newExecutions,
      customerLogs: newCustomerLogs,
      pageInfo,
    } = pollQuery.data

    const hasNewTimelineData =
      newMessages.length > 0 ||
      newTickets.length > 0 ||
      newQuestions.length > 0 ||
      newOpportunities.length > 0 ||
      newExecutions.length > 0 ||
      newCustomerLogs.length > 0

    if (!hasNewTimelineData) return

    const key = getConversationTimelineQueryKey(conversationId, includeRaw)

    queryClient.setQueryData<InfiniteData<ConversationTimelinePage>>(key, (oldData) => {
      if (!oldData || oldData.pages.length === 0) return oldData

      const firstPage = oldData.pages[0]!
      const updatedFirstPage: ConversationTimelinePage = {
        ...firstPage,
        messages: dedupeById([...firstPage.messages, ...newMessages]),
        conversationTickets: dedupeById([...firstPage.conversationTickets, ...newTickets]),
        conversationQuestions: dedupeById([...firstPage.conversationQuestions, ...newQuestions]),
        conversationOpportunities: dedupeById([
          ...firstPage.conversationOpportunities,
          ...newOpportunities,
        ]),
        conversationCapabilityExecutions: dedupeById([
          ...firstPage.conversationCapabilityExecutions,
          ...newExecutions,
        ]),
        customerLogs: dedupeById([...firstPage.customerLogs, ...newCustomerLogs]),
        pageInfo: {
          ...firstPage.pageInfo,
          newestMessageId: pageInfo.newestMessageId ?? firstPage.pageInfo.newestMessageId,
        },
      }

      const pages = [updatedFirstPage, ...oldData.pages.slice(1)]

      return { ...oldData, pages }
    })
  }, [pollQuery.data, conversationId, includeRaw, queryClient])

  return {
    messages,
    conversationTickets,
    conversationQuestions,
    conversationOpportunities,
    conversationCapabilityExecutions,
    customerLogs,
    isLoading: infiniteQuery.isPending,
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    isPollingNew: pollQuery.isFetching,
  }
}
